import {
  Component,
  computed,
  inject,
  input,
  InputSignal,
  Signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  EEntityType,
  EOrderEventType,
  EOrderStatus,
  EStatusChipType,
  ERelationType,
  INormalText,
  IOrderEvent,
  ORDER_EVENT_PRIORITY,
} from '@web/dtos';
import { FilterTextsPipe } from '../../../pipes/filterTexts/filterTexts.pipe';
import { LanguagePipe } from '../../../pipes/language/language.pipe';
import { CorporateDesignService } from '../../../services/corporate-design/corporate-design.service';
import { WebTextStore } from '../../../services/api/text/web-text-store.service';

@Component({
  selector: 'aw-order-status-chip',
  imports: [CommonModule, FilterTextsPipe, LanguagePipe],
  templateUrl: './order-status-chip.component.html',
  styles: '',
})
export class OrderStatusChipComponent {
  public readonly orderEvents: InputSignal<IOrderEvent[]> = input.required();
  public readonly id: InputSignal<string> = input.required();
  public readonly type: InputSignal<EStatusChipType> = input(
    EStatusChipType.icon as EStatusChipType
  );

  private readonly textStore: WebTextStore = inject(WebTextStore);
  protected readonly corporateDesign: CorporateDesignService = inject(
    CorporateDesignService
  );
  protected readonly orderStatus: Signal<EOrderStatus> = computed(() =>
    this.toOrderStatus(this.orderEvents())
  );
  protected readonly statusRelation: Signal<ERelationType> = computed(() =>
    this.statusToRelationType(this.orderStatus())
  );

  protected readonly texts: Signal<INormalText[]> = this.textStore.texts;

  protected readonly EEntityType: typeof EEntityType = EEntityType;
  protected readonly EOrderStatus: typeof EOrderStatus = EOrderStatus;
  protected readonly EStatusChipType: typeof EStatusChipType = EStatusChipType;

  protected toOrderStatus(events: IOrderEvent[]): EOrderStatus {
    if (events.some(event => event.eventType === EOrderEventType.refunded)) {
      return EOrderStatus.rejected;
    }
    if (!events.length) {
      return EOrderStatus.complete;
    }

    const latestEvent = events.reduce((latest, event) =>
      this.isAfter(event, latest) ? event : latest
    );
    if (latestEvent.eventType === EOrderEventType.salesDataCreated) {
      return EOrderStatus.complete;
    }
    if (latestEvent.eventType === EOrderEventType.paymentReserved) {
      return EOrderStatus.pendingPaymentVerification;
    }
    return EOrderStatus.inProgress;
  }

  private isAfter(event: IOrderEvent, reference: IOrderEvent): boolean {
    const eventTime = Date.parse(event.createdAt);
    const referenceTime = Date.parse(reference.createdAt);
    const normalizedEventTime = Number.isFinite(eventTime) ? eventTime : 0;
    const normalizedReferenceTime = Number.isFinite(referenceTime)
      ? referenceTime
      : 0;
    if (normalizedEventTime !== normalizedReferenceTime) {
      return normalizedEventTime > normalizedReferenceTime;
    }
    return (
      ORDER_EVENT_PRIORITY[event.eventType] >
      ORDER_EVENT_PRIORITY[reference.eventType]
    );
  }

  protected statusToRelationType(status: EOrderStatus): ERelationType {
    switch (status) {
      case EOrderStatus.complete:
        return ERelationType.complete;
      case EOrderStatus.cancelled:
        return ERelationType.cancelled;
      case EOrderStatus.rejected:
        return ERelationType.rejected;
      case EOrderStatus.inProgress:
        return ERelationType.inProgress;
      case EOrderStatus.pendingPaymentVerification:
        return ERelationType.pendingPaymentVerification;
      default:
        return ERelationType.unknown;
    }
  }
}
